<script lang="ts">
import { DoughnutChartConfig } from '@/types/MultiSeries';
import { compactNumber } from '@/utilities/Number';
import { paletteColors } from '@/utilities/Color';
import { DoughnutData, formatDoughnutChartData, FusionChartDoughnut2D } from '@/types/Doughnut';
import { ParentAccountCompany } from '@/types/Account';
import { PortcoProgram } from '@/types/Programs';
import { IsUserAClient } from '@/utilities/AuthUtils';
import { AppState } from '@/store';

export default {
  name: 'ParentAccountOverview',
};

interface Month {
  label: string | null;
  programs: PortcoProgram[];
  key: string;
}

const chartDefault: DoughnutChartConfig = {
  animation: '1',
  centerLabelFontSize: 15,
  decimals: 0,
  doughnutRadius: '55%',
  forceYAxisValueDecimals: false,
  paletteColors: paletteColors,
  showBorder: false,
  showLegend: true,
  theme: 'cacs',
};

function premiumByCoverageTypeDataSource(data: DoughnutData[]): FusionChartDoughnut2D {
  const total = compactNumber(data.reduce((acc, r) => acc + r.value, 0));
  return {
    chart: {
      ...chartDefault,
      exportFilename: 'Premium by Coverage',
      defaultCenterLabel: `Total<br><b>$${total}</b>`,
      centerLabel: '$label <b>$$value</b>',
    },
    data: formatDoughnutChartData(data.map((d) => ({ ...d, displayValue: compactNumber(d.value) }))),
  };
}

function premiumByCarrierDataSource(data: DoughnutData[]): FusionChartDoughnut2D {
  const total = compactNumber(data.reduce((acc, r) => acc + r.value, 0));

  return {
    chart: {
      ...chartDefault,
      exportFilename: 'Premium by Carrier',
      defaultCenterLabel: `Total<br><b>$${total}</b>`,
      centerLabel: '<b>$label</b> $$value',
      captionFontSize: 20,
      captionAlignment: 'center',
    },
    data: formatDoughnutChartData(data.map((d) => ({ ...d, displayValue: compactNumber(d.value) }))),
  };
}

function premiumByCompanyDataSource(data: DoughnutData[]): FusionChartDoughnut2D {
  const total = compactNumber(data.reduce((acc, r) => acc + r.value, 0));

  return {
    chart: {
      ...chartDefault,
      exportFilename: 'Premium by Company',
      defaultCenterLabel: `Total<br><b>$${total}</b>`,
      centerLabel: '<b>$label</b> $$value',
      captionFontSize: 20,
      captionAlignment: 'center',
    },
    data: formatDoughnutChartData(data.map((d) => ({ ...d, displayValue: compactNumber(d.value) }))),
  };
}
</script>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { parentAccountService, referenceService } from '@/services/api';
import { policyTypeService } from '@/services/PolicyTypeService';
import { useRoute } from 'vue-router';
import { MultiSeries } from '@/types/MultiSeries';
import Toggle from '@/components/v2/Toggle.vue';
import { tenantName } from '@/utilities/AuthUtils';
import { compactDisplayValueForDoughnutChart } from '@/types/Doughnut';
import { filters } from '@/utilities/Filters';
import { DateTime } from 'luxon';
import { useStore } from 'vuex';
import { LabelFilterType } from '@/types/components/FilterType';
import GenericFilter from '@/components/GenericFilter.vue';

const route = useRoute();
const store = useStore<AppState>();

// -----
// data
// -----
const loading = ref(true);
const showExternal = ref(false);
const showNonRecurring = ref(true);
const premiumByCoverageData = ref({} as MultiSeries);
const premiumByCarrierData = ref({} as MultiSeries);
const premiumByCompanyData = ref<DoughnutData[]>([]);
const premiumByCoverageOrCarrier = ref(0);
const companyData = ref<Array<ParentAccountCompany>>([]);
const expirationsByMonth = ref([] as Month[]);
const selectedCoverageTypes = ref<string[]>([]);
const coverageTypes = ref<LabelFilterType<string>[]>([]);
const coverageTypeToProductName = ref(new Map<number, string>());

// ---------
// computed
// ---------
const showExternalLabel = computed(() => {
  return tenantName() !== '' ? `Show Non-${tenantName()} Policies` : 'Show External Policies';
});

const premiumByCoverageType = computed(() => {
  return premiumByCoverageTypeDataSource(transformMultiSeriesToDoughnut(premiumByCoverageData.value));
});

const premiumByCarrier = computed(() => {
  return premiumByCarrierDataSource(transformMultiSeriesToDoughnut(premiumByCarrierData.value));
});

const premiumByCompany = computed(() => {
  return premiumByCompanyDataSource(premiumByCompanyData.value);
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

// ---------
// watchers
// ---------
watch(showExternal, () => {
  refresh();
});

watch(showNonRecurring, () => {
  refresh();
});

// ----------
// functions
// ----------

async function refresh() {
  loading.value = true;

  const now = DateTime.local();
  const accountId = route.params.id as string;

  let queryParams = {} as Record<string, any>;
  if (selectedCoverageTypes.value) queryParams['coverageType'] = selectedCoverageTypes.value;

  queryParams['external'] = showExternal.value.toString();
  queryParams['showNonRecurring'] = showNonRecurring.value.toString();
  queryParams['runoff'] = 'true';
  queryParams['groupRunoff'] = 'true';

  const [coverageData, carrierData, allCompaniesData, companyPremiumData, programsExpiring, coverageTypesRefData, coverageTypesWithProducts] = await Promise.all([
    parentAccountService.premiumByCoverageType(accountId, { ...queryParams }),
    parentAccountService.premiumByCarrier(accountId, queryParams),
    parentAccountService.companies(accountId, queryParams),
    compactDisplayValueForDoughnutChart(parentAccountService.premiumByCompany(accountId, queryParams)),
    parentAccountService.programExpiration(
      accountId,
      now.toISODate(),
      now.plus({ years: 1 }).toISODate(),
      showExternal.value,
      selectedCoverageTypes.value,
    ),
    referenceService.coverageTypes(),
    policyTypeService.GetDistinctCoverageTypes().catch(() => []),
  ]);

  // Create mapping from coverage type to product name
  coverageTypeToProductName.value = (coverageTypesWithProducts || []).reduce((map, ct) => {
    if (ct && ct.coverageTypeId && ct.productName) {
      map.set(ct.coverageTypeId, ct.productName);
    }
    return map;
  }, new Map<number, string>());

  premiumByCoverageData.value = coverageData;
  premiumByCarrierData.value = carrierData;
  premiumByCompanyData.value = companyPremiumData;
  companyData.value = allCompaniesData
    .filter((c) => !c.isParent)
    .sort((c1, c2) => (c1.displayName || c1.accountName || '').localeCompare((c2.displayName || c2.accountName || '')));
  const parent = allCompaniesData.find((c) => c.isParent);
  if (parent) companyData.value.unshift(parent);

  // filter coverage types based on data on initial page load only
  if (coverageTypes.value.length === 0) {
    let coverageTypesFromData: string[] = [];
    coverageTypesFromData = [
      ...premiumByCoverageData.value.categories.flatMap(categoryList => categoryList?.category?.map(label => label.label) ?? '')
    ];

    coverageTypes.value = coverageTypesRefData.filter(e => {
      return coverageTypesFromData.includes(e.label);
    }).map(t => ({ label: t.label, value: t.label }));
  }

  const monthMap = programsExpiring.reduce((monthMap, pe) => {
    const expiresAt = DateTime.fromISO(pe.expiresAt, { zone: 'UTC' });
    const key = `${expiresAt.monthShort}-${expiresAt.year}`;

    if (!monthMap.has(key)) monthMap.set(key, { key, label: expiresAt.monthShort, programs: [] });
    monthMap.get(key)?.programs.push(pe);
    return monthMap;
  }, new Map<string, Month>());

  let n = now;
  let expirations = [] as Month[];

  let year = -1;
  for (let i = 0; i < 12; i++) {
    const key = `${n.monthShort}-${n.year}`;
    const month = monthMap.get(key) || { key, label: n.monthShort, programs: [] };

    // Only 1 month of each year displays year suffix:
    // FEB 2023  MAR  APR  ...  DEC  JAN 2024
    if (year !== n.year) {
      month.label += ` ${n.year}`;
      year = n.year;
    }

    // remove duplicates where multiple programs exist for a given month for a given portco and department
    month.programs = [
      ...month.programs
        .reduce((acc, p) => {
          const key = `${p.accountId}-${p.department}`;
          if (acc.has(key)) return acc;
          acc.set(key, p);
          return acc;
        }, new Map<string, PortcoProgram>())
        .values(),
    ];

    month.programs.sort((p1, p2) => p1.accountName.localeCompare(p2.accountName));
    expirations.push(month);

    n = n.plus({ month: 1 });
  }

  expirationsByMonth.value = [...expirations];

  loading.value = false;
}

function getProductNameForProgram(program: PortcoProgram): string {
  if (program.coverageTypeIds && program.coverageTypeIds?.length > 1) {
    return 'Multiple';
  }

  else if (program.coverageTypeIds && program.coverageTypeIds.length === 1) {
    // Try to map department to product name
    const productName = coverageTypeToProductName.value.get(program.coverageTypeIds[0]);
    if (productName) {
      return productName;
    }
  }
  
  // Fall back to department if no product name found
  return program.department;
}

function transformMultiSeriesToDoughnut(data: MultiSeries): DoughnutData[] {
  const categories = new Map<number, string>();

  if (data.categories) {
    data.categories[0].category?.forEach((cat, idx) => {
      categories.set(idx, cat.label);
    });
  }

  const totals = new Map<number, number>();
  data.dataset.forEach((dataset) => {
    dataset.data.forEach((data, idx) => {
      if (!totals.has(idx)) {
        totals.set(idx, 0);
      }
      totals.set(idx, (totals.get(idx) || 0) + (data.value || 0));
    });
  });

  return Array.from(totals.values()).map((value, idx) => ({ label: categories.get(idx), value: value }));
}

onMounted(() => {
  refresh();
});
</script>

<template>
  <div>
    <div class="d-flex filter-menu">
      <div class="filter-options tw:flex tw:w-full tw:justify-between tw:items-center tw:px-3 tw:py-2">
        <GenericFilter title="Coverage Type" v-model="selectedCoverageTypes" :types="coverageTypes" @update:model-value="refresh"/>
        <div class="tw:flex tw:gap-2">
          <toggle id="show-non-recurring" v-model="showNonRecurring" label="Show Non-Recurring Policies" />
          <toggle id="show-external" v-model="showExternal" :label="showExternalLabel" />
        </div>
      </div>
    </div>

    <div v-if="loading" class="tw:flex tw:justify-center">
      <span class="tw:loading tw:loading-dots tw:loading-lg tw:mt-24 tw:text-gray-500"></span>
    </div>

    <div v-if="!loading">
      <div
        class="tw:mb-4 tw:grid tw:grid-cols-3 tw:justify-center tw:gap-3 tw:p-4 tw:max-xl:grid-cols-2 tw:max-sm:grid-cols-1"
      >
        <!-- doughnut charts-->
        <!-- premium by coverage or carrier chart-->
        <div class="tw:flex tw:flex-col tw:justify-start tw:px-1">
          <h3 class="tw:mx-auto tw:my-0">
            {{ premiumByCoverageOrCarrier === 0 ? 'Premium by Coverage' : 'Premium by Carrier' }}
          </h3>
          <div class="tw:grow-0">
            <fusioncharts
              type="doughnut2d"
              width="100%"
              height="450"
              dataFormat="json"
              :dataSource="premiumByCoverageOrCarrier === 0 ? premiumByCoverageType : premiumByCarrier"
            />
          </div>
          <div class="tw:join tw:justify-center">
            <input
              type="radio"
              name="options"
              aria-label="Coverage"
              class="tw:btn tw:join-item tw:btn-sm"
              @click="premiumByCoverageOrCarrier = 0"
              :checked="premiumByCoverageOrCarrier === 0"
            />

            <input
              type="radio"
              name="options"
              aria-label="Carrier"
              class="tw:btn tw:join-item tw:btn-sm"
              @click="premiumByCoverageOrCarrier = 1"
              :checked="premiumByCoverageOrCarrier === 1"
            />
          </div>
        </div>

        <!-- premium company chart-->
        <div class="tw:flex tw:flex-col tw:justify-start tw:px-1">
          <h3 class="tw:mx-auto tw:my-0">Premium By Company</h3>
          <div class="tw:grow-0">
            <fusioncharts
              type="doughnut2d"
              width="100%"
              height="450"
              dataFormat="json"
              :dataSource="premiumByCompany"
            />
          </div>
        </div>

        <!-- companies table -->
        <div class="tw:flex tw:flex-col">
          <template v-if="(companyData || []).length > 0">
            <div class="tw:mb-4 tw:flex tw:justify-center">
              <h3 class="tw:m-0 tw:mx-auto">Companies</h3>
            </div>

            <div class="tw:max-h-[400px] tw:overflow-auto tw:m-4 tw:pt-0">
              <div class="tw:relative">
                <table class="apptable dense">
                  <thead>
                    <tr>
                      <td class="tw:p-4 nosort tw:rounded-tl-md tw:rounded-bl-md">Company</td>
                      <td class="tw:p-4 nosort tw:text-center">Client Since</td>
                      <td class="tw:p-4 nosort">Industry</td>
                      <td class="tw:p-4 nosort tw:text-center tw:rounded-tr-md tw:rounded-br-md">Premium</td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="company in companyData" :key="company.accountId">
                      <td>
                        <div class="tw:text-sm">
                          <router-link
                            :to="`/account/${company.accountId}/overview`"
                            class="url-text tw:link-hover tw:text-sm tw:text-black"
                            >{{ company.displayName || company.accountName }}</router-link
                          >
                        </div>
                        <div
                          v-if="company.accountStatus === 'P'"
                          class="tw:mt-1 tw:w-fit tw:justify-center tw:rounded-xl tw:p-0.5 tw:px-1.5 tw:text-center tw:text-[0.7em] tw:font-bold tw:uppercase tw:text-white tw:outline tw:outline-1 tw:bg-capital-blue"
                        >
                          {{ isUserAClient ? 'Non-CAC' : 'Prospect' }}
                        </div>
                        <div v-if="company.domainName" class="tw:link tw:text-xs">
                          <a
                            v-if="company.domainName"
                            :href="'//' + `${company.domainName.trim()}`"
                            target="_blank"
                            rel="noopener noreferrer"
                            >{{ company.domainName }}</a
                          >
                        </div>
                      </td>
                      <td class="tw:text-center">{{ filters.date(company.clientSince ?? '') }}</td>
                      <td>{{ company.topLevelIndustry }}</td>
                      <td class="tw:text-center">{{ filters.compactMoney(company.policyPremium) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>
      </div>

      <h3 class="tw:text-center">Upcoming Expirations</h3>

      <div class="tw:mx-auto tw:mb-4 tw:mt-4 tw:flex tw:justify-center">
        <div
          class="tw:mx-4 tw:grid tw:grid-cols-2 tw:lg:grid-cols-4 tw:lg:max-w-[1200px] tw:2xl:grid-cols-6 tw:2xl:max-w-[1800px]  tw:justify-center tw:rounded-md tw:border tw:border-solid tw:border-gray-300 tw:lg:border-l-0 tw:text-center"
        >
          <div
            v-for="(month, index) in expirationsByMonth"
            :key="month.key"
            class="tw:min-h-48 tw:border-0 tw:border-solid tw:border-gray-300"
						:class="{ 'tw:border-r-1 tw:lg:border-l-1': index % 2 === 0 }"
          >
            <div
              class="tw:border-0 tw:border-b-1 tw:border-solid tw:border-gray-300 tw:bg-gray-100 tw:p-1 tw:py-2 tw:text-xs tw:font-bold tw:uppercase tw:text-gray-600"
            >
              {{ month.label }}
            </div>

            <div class="tw:min-h-20 tw:p-1 tw:pb-4">
              <template v-for="program in month.programs" :key="program.accountId">
                <router-link :to="`/account/${program.accountId}/policy-detail`">
                  <div
                    class="tw:m-1 tw:rounded-md tw:border tw:border-(--chip-color) tw:px-1 tw:py-0.5 tw:text-xs tw:font-bold tw:text-(--chip-color) tw:hover:bg-blue-100"
                  >
                    {{ program.accountName }}: {{ getProductNameForProgram(program) }}
                  </div>
                </router-link>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
